import React from 'react';
import { FormattedNumber } from 'react-intl';

import { isCreditCardPaymentMethod } from './PaymentHelpers';
import { shouldHandleCreditFloorDecline } from './creditFloorHelpers';
import { translateAdyenStatusToErrorMsg } from './utils';

import type { Error } from 'types/Error';
import type { FeatureFlags } from 'types/FeatureFlags';
import type { Payment } from 'types/Payment';

import { isPaypalAppconnectDeclinedOrBlocked } from 'businessLogic/Payment/Common';
import MessageCard from 'components/Shared/MessageCard/MessageCard';
import SegmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import {
  gettoLocaleTimeString,
  getNanopayBlockDueDate,
  isNanoPayBankBlock,
  getPayPalAppConnectBlockDueDate,
  isPayPalAppConnectBlock,
} from 'shared/clientUtils';
import { TXN_MAP, PAYMENT_DECLINE_REASON_CODES } from 'types/constants';

const splunkReporter = SplunkReporter.getInstance();

export const getPaymentErrorMessage = ({
  paymentStatus,
  paymentError,
  error,
  companyName,
  featureFlags,
  paymentMethodType,
  payPalProcessor,
  logger,
  isAutoPayOn,
  isDateScheduled,
  region,
  activeSubscriptionPaymentErrorType,
  creditFloorDeclineAmount,
  currency,
  transactionType,
  isBatchPaymentsEnabled,
  onEditAmountClick,
  partialPaymentEnabled,
  isPaymentRetryExperimentTreatmentA,
  isPaymentRetryExperimentTreatmentB,
  isInBillingAddressExperiment,
  onPaymentRetryErrorViewed,
  onPaymentRetryWorkflowErrorViewed,
  onBillingInfoErrorViewed,
}: {
  error?: Error;
  paymentStatus: Payment['paymentStatus'];
  paymentError?: Payment['paymentError'];
  companyName: string;
  featureFlags: FeatureFlags;
  paymentMethodType: Payment['paymentMethodType'];
  payPalProcessor?: Payment['payPalProcessor'];
  logger: string;
  isAutoPayOn: Payment['isAutoPayOn'];
  isDateScheduled: Payment['isDateScheduled'];
  region: string;
  activeSubscriptionPaymentErrorType?: 'DECLINE' | 'MISSING_METHOD';
  creditFloorDeclineAmount?: number | null;
  currency?: string;
  transactionType?: string;
  isBatchPaymentsEnabled?: boolean;
  onEditAmountClick?: (options?: { skipTracking?: boolean }) => void;
  partialPaymentEnabled?: boolean;
  isPaymentRetryExperimentTreatmentA?: boolean;
  isPaymentRetryExperimentTreatmentB?: boolean;
  isInBillingAddressExperiment?: boolean;
  onPaymentRetryErrorViewed?: () => void;
  onPaymentRetryWorkflowErrorViewed?: () => void;
  onBillingInfoErrorViewed?: () => void;
}) => {
  let header, text: string | undefined;
  let status: 'error' | 'info' | 'warn' = 'error';
  let messageValues:
    | Record<string, string | React.JSX.Element | ((chunks: string) => React.JSX.Element)>
    | undefined;

  // Check if payment was declined
  const isPaymentDeclined = paymentStatus === TXN_MAP.STATUS.DECLINED;

  // Only show enhanced decline message if editing amount is allowed (mirrors PaymentAmountInput.onLabelClick guards)
  const isEditAmountAllowed = partialPaymentEnabled && !isDateScheduled && !isAutoPayOn;
  const hasCreditFloorDeclineAmt = creditFloorDeclineAmount && creditFloorDeclineAmount > 0;
  // Show enhanced decline message only for users in the Payment Retry experiment treatment group
  // with the corresponding feature flag enabled. Control group and non-bucketed users see the generic decline message.
  // Credit floor decline - show enhanced error message with partial payment link
  // Use creditFloorDeclineAmount presence as the indicator (persists through payment method changes)
  // Note: Require specific treatment (with feature flag) to avoid edge case where experiment is running
  // but feature flag is disabled, which would leave text/messageValues undefined
  const hasDeclineAmount =
    (isPaymentRetryExperimentTreatmentA || isPaymentRetryExperimentTreatmentB) &&
    hasCreditFloorDeclineAmt;

  // Check if this is an INFO_INCORRECT decline (card/billing details issue)
  // Uses BillingAddressExperiment - same experiment that controls showing billing address fields
  // Also requires the billing address feature flag to be enabled
  const isInfoIncorrectDecline =
    featureFlags?.['SBSEG-CP-billing-address-enabled'] &&
    isInBillingAddressExperiment &&
    (paymentError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.INFO_INCORRECT ||
      paymentError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.INFO_INCORRECT_V2);

  // Credit floor decline (SMALLER_PAYMENT) - requires partial payment option
  // Supports legacy lowercase and new uppercase values during SCS rollout
  // Explicitly check reason codes to prevent other decline types (like INFO_INCORRECT) from falling through
  const isCreditFloorDecline =
    hasDeclineAmount &&
    (paymentError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.SMALLER_PAYMENT ||
      paymentError?.reasonCode === PAYMENT_DECLINE_REASON_CODES.SMALLER_PAYMENT_V2);

  if (
    isPaymentDeclined &&
    transactionType === TXN_MAP.TYPES.INVOICE &&
    !isBatchPaymentsEnabled &&
    isInfoIncorrectDecline
  ) {
    // Track that the billing info error banner is being shown
    onBillingInfoErrorViewed?.();

    header = 'PAYFLOW_INFO_INCORRECT_DECLINE_HEADER';
    text = 'PAYFLOW_INFO_INCORRECT_DECLINE_TEXT';

    messageValues = {
      amount: (
        <FormattedNumber
          value={creditFloorDeclineAmount ?? 0}
          style="currency"
          currency={currency || 'USD'}
        />
      ),
    };
  } else if (
    shouldHandleCreditFloorDecline({
      paymentStatus: paymentStatus as any,
      transactionType: transactionType as any,
      isBatchPaymentsEnabled: isBatchPaymentsEnabled as any,
      isEditAmountAllowed: isEditAmountAllowed as any,
      isCreditFloorDecline: isCreditFloorDecline as any,
    })
  ) {
    header = 'PAYFLOW_CREDIT_FLOOR_DECLINE_HEADER';

    // Use different text based on treatment
    // TreatmentA: Show text with actionable link
    // TreatmentB: Show text without link (plain text)
    if (isPaymentRetryExperimentTreatmentA) {
      // Track that the payment retry error banner is being shown
      onPaymentRetryErrorViewed?.();
      text = 'PAYFLOW_CREDIT_FLOOR_DECLINE_TEXT';
      messageValues = {
        // Include amount for credit floor declines
        ...(hasCreditFloorDeclineAmt
          ? {
              amount: (
                <FormattedNumber
                  value={creditFloorDeclineAmount}
                  style="currency"
                  currency={currency || 'USD'}
                />
              ),
            }
          : {}),
        actionPartialPayment: (chunks: string) => (
          <a
            className="action-link"
            onClick={(e) => {
              e.preventDefault();
              // Track that the user clicked the partial payment link
              SegmentIO.paymentRetryMessageSelected('partial_payment');
              // Skip downstream tracking since we already tracked above
              onEditAmountClick?.({ skipTracking: true });
            }}
          >
            {chunks}
          </a>
        ),
      };
    } else if (isPaymentRetryExperimentTreatmentB) {
      // Track that the payment retry workflow error banner is being shown for treatment B
      onPaymentRetryWorkflowErrorViewed?.();
      text = 'PAYFLOW_CREDIT_FLOOR_DECLINE_TEXT_NO_LINK';
      messageValues = {
        // Include amount for credit floor declines (no action link for treatmentB)
        ...(hasCreditFloorDeclineAmt
          ? {
              amount: (
                <FormattedNumber
                  value={creditFloorDeclineAmount}
                  style="currency"
                  currency={currency || 'USD'}
                />
              ),
            }
          : {}),
      };
    }
  } else if (paymentError && paymentError.message && paymentStatus === TXN_MAP.STATUS.DECLINED) {
    header = `PAYFLOW_${paymentStatus}_ERROR_HEADER`;
    text = `PAYFLOW_${paymentError.message}_ERROR_TEXT`;
  } else if (featureFlags && featureFlags['block-payments']) {
    header = 'BLOCK_PAYMENTS_HEADER';
    text = 'BLOCK_PAYMENTS_TEXT';
  } else if (
    paymentMethodType === 'pp' &&
    payPalProcessor &&
    isPaypalAppconnectDeclinedOrBlocked(featureFlags, paymentStatus, payPalProcessor)
  ) {
    header = 'PAYFLOW_PAYPAL_APPCONNECT_ERROR_HEADER';
    text = 'PAYFLOW_PAYPAL_APPCONNECT_ERROR_MESSAGE';
    status = 'warn';
  } else if (error && error.title) {
    header = error.title;
    text = error.body;
    messageValues = error.i18nProps;
  } else if (
    paymentError &&
    paymentError.message &&
    paymentError.message === 'PAYFLOW_VALIDATION_CARD_TYPE_INVALID'
  ) {
    text = paymentError.message;
    messageValues = paymentError;
  } else if (
    paymentError &&
    paymentError.message &&
    paymentError.message === 'AFFIRM_BNPL_PROCESSING'
  ) {
    header = 'PAYFLOW_AFFIRM_BNPL_PROCESSING_HEADER';
    text = 'PAYFLOW_AFFIRM_BNPL_PROCESSING_MESSAGE';
    status = 'info';
  } else if (paymentError && paymentError.message) {
    header = `PAYFLOW_${paymentError.message}_HEADER`;
    text = `PAYFLOW_${paymentError.message}_MESSAGE`;
    messageValues = { 0: companyName };
  } else if (isPayPalAppConnectBlock(paymentMethodType, payPalProcessor, featureFlags)) {
    header = 'BLOCK_PAYMENTS_PAYPAL_HEADER';
    text = 'BLOCK_PAYMENTS_PAYPAL_TEXT';

    // get due date
    const blockingDueDate = getPayPalAppConnectBlockDueDate(featureFlags);
    if (blockingDueDate > 0) {
      header = 'BLOCK_PAYMENTS_PAYPAL_HEADER_DUE_DATE';
      text = 'BLOCK_PAYMENTS_DUE_DATE';
      const localeTimeString = gettoLocaleTimeString(blockingDueDate);
      messageValues = { dueTime: localeTimeString };

      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'viewSale',
        action: 'render',
        activityInfo: {
          isBlockWithDueDate: true,
          blockingDueDate,
          localeTimeString,
          source: 'PayPal Appconnect',
        },
      });
    }

    // block bank nanopay
  } else if (isNanoPayBankBlock(paymentMethodType, featureFlags)) {
    const blockingDueDate = getNanopayBlockDueDate(featureFlags);
    header = 'BLOCK_PAYMENTS_NANOPAY_BANK_HEADER_DUE_DATE';
    text = 'BLOCK_PAYMENTS_DUE_DATE';
    const localeTimeString = gettoLocaleTimeString(blockingDueDate);
    messageValues = { dueTime: localeTimeString };

    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'viewSale',
      action: 'render',
      activityInfo: {
        isBlockWithDueDate: true,
        blockingDueDate,
        localeTimeString,
        source: 'bank nanopay',
      },
    });
  } else if (paymentMethodType === 'pbb') {
    text = translateAdyenStatusToErrorMsg(paymentStatus, paymentMethodType) as string;
    status = 'warn';
  } else if (isCreditCardPaymentMethod(paymentMethodType) && region === 'GB') {
    text = translateAdyenStatusToErrorMsg(paymentStatus, paymentMethodType) as string;
    status = 'warn';
  } else if (activeSubscriptionPaymentErrorType) {
    header = `SUBSCRIPTION_ACTIVE_ERROR_HEADER`;
    text = `SUBSCRIPTION_ACTIVE_${activeSubscriptionPaymentErrorType}_TEXT`;
  }
  const paymentType = isDateScheduled ? 'schedule' : isAutoPayOn ? 'autopay' : 'payment';

  const activityInfo = { paymentType, paymentMethod: paymentMethodType, screen: 'payable' };

  return text ? (
    <MessageCard
      activityInfo={activityInfo}
      header={header}
      text={text}
      status={status}
      messageValues={messageValues}
    />
  ) : null;
};
